import React from "react";
import { TopBar } from "@/components/TopBar";
import {
  apiListCustomers,
  apiCreateCustomer,
  apiUpdateCustomer,
  formatApiErrorDetail,
} from "@/lib/api";
import { Loader2, Plus, Save, X, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import LinkPublik from "@/components/LinkPublik";
import CustomerBaruPanel from "@/components/CustomerBaruPanel";

const EMPTY = { nama: "", nomor_telepon: "", email: "", alamat: "", catatan: "", tipe_customer: "ENDUSER",
  nama_instansi: "", pic_nama: "", npwp: "", is_instansi: false };
const TIPE = [
  { value: "ENDUSER", label: "Enduser" },
  { value: "RESELLER", label: "Reseller" },
  { value: "MEMBER", label: "Member" },
];
const TIPE_LABEL = Object.fromEntries(TIPE.map((t) => [t.value, t.label]));
// Owner 2026-10-15 (A4): Kasir/CS may CREATE a Reseller/Member customer; changing the tier of an
// existing customer stays with the owner (enforced server-side).
const OWNER_ONLY_TIPE = ["RESELLER", "MEMBER"];

export default function CustomerManagement() {
  const { user } = useAuth();
  const isOwner = user?.role === "admin";
  const readOnly = user?.role === "desain"; // Desain: read-only access [ASUMSI]
  const [rows, setRows] = React.useState([]);
  const [originalTipe, setOriginalTipe] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [notice, setNotice] = React.useState(null);
  const [form, setForm] = React.useState(EMPTY);
  const [editingId, setEditingId] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await apiListCustomers());
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const flash = (m) => { setNotice(m); setTimeout(() => setNotice(null), 2500); };
  const resetForm = () => { setForm(EMPTY); setEditingId(null); setOriginalTipe(null); };
  const tipeSelectable = (t) => isOwner || !editingId || !OWNER_ONLY_TIPE.includes(t) || t === originalTipe;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    const payload = {
      nama: form.nama,
      nomor_telepon: form.nomor_telepon,
      email: form.email || null,
      alamat: form.alamat || null,
      catatan: form.catatan || null,
      tipe_customer: form.tipe_customer,
      // Addendum 5: institution data (optional); the flag itself only by the owner
      nama_instansi: form.nama_instansi || null,
      pic_nama: form.pic_nama || null,
      npwp: form.npwp || null,
      ...(isOwner ? { is_instansi: !!form.is_instansi } : {}),
    };
    try {
      if (editingId) { await apiUpdateCustomer(editingId, payload); flash("Customer diperbarui"); }
      else {
        const c = await apiCreateCustomer(payload);
        // paket 1b (B-10): the same name + phone already exists -> that customer is used, nothing new
        flash(c.sudah_ada ? `Customer sudah ada: ${c.kode_customer} ${c.nama} — data lama dipakai, tidak dibuat baru` : "Customer ditambahkan");
      }
      resetForm();
      await load();
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({ nama: c.nama, nomor_telepon: c.nomor_telepon, email: c.email || "", alamat: c.alamat || "", catatan: c.catatan || "", tipe_customer: c.tipe_customer || "ENDUSER",
      nama_instansi: c.nama_instansi || "", pic_nama: c.pic_nama || "", npwp: c.npwp || "", is_instansi: !!c.is_instansi });
    setOriginalTipe(c.tipe_customer || "ENDUSER");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const inputCls = "mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60";
  const labelCls = "text-[13px] font-semibold text-slate-200";

  return (
    <div data-testid="customer-management-page" className="min-h-screen bg-slate-950 text-slate-200">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">Manajemen Customer</h1>
          <p className="text-sm text-slate-400 mt-1">Data pelanggan transaksi (tanpa akun login).</p>
        </div>

        {notice && <div data-testid="cust-notice" className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm text-emerald-300">{notice}</div>}
        {error && <div data-testid="cust-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</div>}

        {!readOnly && <form data-testid="customer-form" onSubmit={onSubmit} className="rounded-xl border border-slate-800 bg-slate-900/60 p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Nama</label>
            <input data-testid="cust-nama" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} required className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Nomor Telepon</label>
            <input data-testid="cust-telepon" value={form.nomor_telepon} onChange={(e) => setForm({ ...form, nomor_telepon: e.target.value })} required className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Tipe Customer</label>
            <select data-testid="cust-tipe" value={form.tipe_customer} onChange={(e) => setForm({ ...form, tipe_customer: e.target.value })} className={inputCls}>
              {TIPE.map((t) => <option key={t.value} value={t.value} disabled={!tipeSelectable(t.value)}>{t.label}</option>)}
            </select>
            {!isOwner && <p data-testid="cust-tipe-note" className="mt-1 text-[11px] text-slate-500">{editingId ? "Mengubah tipe customer lama hanya Owner." : "Customer baru boleh langsung Reseller/Member (tercatat, dicek Owner)."}</p>}
          </div>
          <div>
            <label className={labelCls}>Email (opsional)</label>
            <input data-testid="cust-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Alamat lengkap (opsional; wajib untuk invoice instansi)</label>
            <input data-testid="cust-alamat" value={form.alamat} onChange={(e) => setForm({ ...form, alamat: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Catatan (opsional)</label>
            <input data-testid="cust-catatan" value={form.catatan} onChange={(e) => setForm({ ...form, catatan: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Nama perusahaan / instansi (opsional)</label>
            <input data-testid="cust-nama-instansi" value={form.nama_instansi} onChange={(e) => setForm({ ...form, nama_instansi: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Nama PIC (opsional)</label>
            <input data-testid="cust-pic" value={form.pic_nama} onChange={(e) => setForm({ ...form, pic_nama: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>NPWP (opsional, 15/16 digit)</label>
            <input data-testid="cust-npwp" value={form.npwp} onChange={(e) => setForm({ ...form, npwp: e.target.value })} placeholder="01.234.567.8-901.000" className={`${inputCls} font-mono`} />
          </div>
          <div className="flex items-end">
            <label className={`inline-flex items-center gap-2 text-sm ${isOwner ? "text-slate-200" : "text-slate-500"}`} title={isOwner ? "" : "Penanda Instansi hanya bisa diubah Owner"}>
              <input type="checkbox" data-testid="cust-instansi" checked={!!form.is_instansi} disabled={!isOwner}
                onChange={(e) => setForm({ ...form, is_instansi: e.target.checked })} />
              Pelanggan Instansi (invoice + PPN){!isOwner && " — diatur Owner"}
            </label>
          </div>
          <div className="flex items-end lg:col-span-3">
            <div className="ml-auto flex items-center gap-2">
              {editingId && <button type="button" data-testid="cust-cancel" onClick={resetForm} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:text-slate-100"><X size={15} /> Batal</button>}
              <button data-testid="cust-submit" type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-5 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60">
                {saving ? <Loader2 className="animate-spin" size={16} /> : editingId ? <Save size={16} /> : <Plus size={16} />}
                {editingId ? "Simpan" : "Tambah"}
              </button>
            </div>
          </div>
        </form>}

        {isOwner && <CustomerBaruPanel />}

        <div className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
            <h2 className="text-[15px] font-bold text-soq-biru-tua">Daftar Customer</h2>
            <button data-testid="cust-refresh" onClick={load} className="inline-flex items-center gap-2 text-xs font-mono text-slate-400 hover:text-slate-200"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh</button>
          </div>
          {loading ? (
            <div className="p-8 flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2" size={18} /> Memuat…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="customer-table">
                <thead>
                  <tr className="text-left text-xs font-semibold text-slate-500 bg-slate-900 border-b border-slate-800">
                    <th className="px-4 py-3">Kode</th>
                    <th className="px-4 py-3">Nama</th>
                    <th className="px-4 py-3">Tipe</th>
                    <th className="px-4 py-3">Telepon</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Alamat</th>
                    <th className="px-4 py-3 text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.id} data-testid={`customer-row-${c.id}`} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                      <td className="px-4 py-3 font-mono text-amber-300">{c.kode_customer || "—"}</td>
                      <td className="px-4 py-3 text-slate-200">{c.nama}</td>
                      <td className="px-4 py-3"><span data-testid={`cust-tipe-${c.id}`} className="text-[10px] font-mono px-2 py-0.5 rounded border border-cyan-500/30 bg-cyan-500/10 text-cyan-300">{TIPE_LABEL[c.tipe_customer] || c.tipe_customer}</span>
                        {c.is_instansi && <span data-testid={`cust-instansi-${c.id}`} title={c.nama_instansi || ""} className="ml-1 text-[10px] font-mono px-2 py-0.5 rounded border border-amber-500 bg-amber-500 text-slate-950 font-semibold">INSTANSI</span>}</td>
                      <td className="px-4 py-3 font-mono text-slate-300">{c.nomor_telepon}</td>
                      <td className="px-4 py-3 text-slate-400">{c.email || "—"}</td>
                      <td className="px-4 py-3 text-slate-400">{c.alamat || "—"}</td>
                      <td className="px-4 py-3 text-right">
                        {!readOnly && <span className="mr-2"><LinkPublik kind="customer" id={c.id} label="Link cek order" testid={`cust-link-${c.id}`} /></span>}
                        {!readOnly && <button data-testid={`cust-edit-${c.id}`} onClick={() => startEdit(c)} className="rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-amber-500/40 hover:text-amber-300">Edit</button>}
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">Belum ada customer.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
